import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  //Divider,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BusinessCenter as BusinessIcon,
  TrendingUp as MarketIcon,
  People as TeamIcon,
  AttachMoney as FinanceIcon,
  Campaign as MarketingIcon,
  Code as TechIcon
} from '@mui/icons-material';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableStepContent from '../common/EditableStepContent.tsx';

// Import other steppers that will be integrated
import StrategicPlanStepper from './StrategicPlanStepper.tsx';
import FinancialPlanStepper from './FinancialPlanStepper.tsx';
import MarketingPlanStepper from './MarketingPlanStepper.tsx';
import ProductStrategyStepper from './ProductStrategyStepper.tsx';
import TechRoadmapStepper from './TechnologyRoadmapStepper.tsx';

interface BusinessPlanStepperProps {
  companyName?: string;
  productName?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const MetricChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(0, 204, 153, 0.2)',
  }
}));

const BusinessPlanStepper: React.FC<BusinessPlanStepperProps> = ({
  companyName = "Savvy Data",
  productName = "AI Analytics Platform",
  initialStep = 0,
  onComplete,
  onSubmitRFP,
}) => {
  // Add state for costs
  const [stepCosts, setStepCosts] = useState({
    0: 3500, // Executive Summary
    1: 2800, // Market Analysis
    2: 3200, // Company & Team
    3: 4500, // Financial Plan
    4: 3600, // Marketing Strategy
    5: 2900, // Technology Roadmap
  });

  // Add state to track sub-stepper visibility
  const [activeSubStepper, setActiveSubStepper] = useState<string | null>(null);

  // Function to update a step's cost
  const handleCostChange = (stepIndex: number, newCost: number) => {
    setStepCosts(prev => ({
      ...prev,
      [stepIndex]: newCost
    }));
  };

  // Function to handle sub-stepper completion
  const handleSubStepperComplete = () => {
    setActiveSubStepper(null);
  };

  // Define business plan steps
  const businessSteps: StepData[] = [
    {
      label: 'Executive Summary',
      description: 'High-level overview of the business plan and company vision',
      icon: <BusinessIcon />,
      cost: stepCosts[0],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Executive Summary: {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Company Overview"
            cost={stepCosts[0]}
            onCostChange={(newCost) => handleCostChange(0, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Savvy Data is an AI-powered data analytics platform designed to help businesses transform their raw data into actionable insights. Our goal is to simplify Artificial Intelligence, making it accessible to businesses of all sizes without requiring specialized data science expertise.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Mission & Vision
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              Our mission is to empower businesses with the insights they need to make data-driven decisions quickly and confidently. Our vision is a world where every business, regardless of size or technical expertise, can harness the full power of their data.
            </Typography>
            
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Key Objectives
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Achieve $2.5M in ARR within first 18 months" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Reach 500 paid business customers by end of year 2" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Establish strategic partnerships with 3 major cloud providers" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              For detailed strategic planning, explore our comprehensive strategic plan:
            </Typography>
            <Paper 
              sx={{ p: 2, cursor: 'pointer', bgcolor: 'rgba(138, 43, 226, 0.1)', 
                '&:hover': { bgcolor: 'rgba(138, 43, 226, 0.2)' } }}
              onClick={() => setActiveSubStepper('strategic')}
            >
              <Typography variant="subtitle1" sx={{ color: '#8A2BE2' }}>
                View Strategic Plan Details →
              </Typography>
            </Paper>
          </Box>
        </Box>
      )
    },
    {
      label: 'Market Analysis',
      description: 'Analysis of target market, competition, and growth trends',
      icon: <MarketIcon />,
      cost: stepCosts[1],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Market Analysis: {productName}
          </GradientText>
          
          <EditableStepContent
            title="Market Size & Growth"
            cost={stepCosts[1]}
            onCostChange={(newCost) => handleCostChange(1, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                The global data analytics market is valued at $49.4 billion in 2023 and is expected to grow at a CAGR of 27.3% to reach $166.8 billion by 2028. This growth is driven by increasing digital transformation initiatives, rising data volumes, and the growing importance of data-driven decision making.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Competitive Landscape
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Enterprise Players
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Established enterprise solutions (Tableau, Power BI, Looker) dominate the high-end market but are often complex and expensive for small and medium businesses.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    SMB Solutions
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Current SMB-focused options lack advanced AI capabilities and often require technical expertise to use effectively.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Typography variant="subtitle1" sx={{ color: '#fff', mt: 3, mb: 1 }}>
              Target Market
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Savvy Data primarily targets small to medium-sized businesses (10-500 employees) across e-commerce, SaaS, healthcare, and financial services industries that need data analytics but lack dedicated data science teams.
            </Typography>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Company & Team',
      description: 'Organization structure, key team members, and hiring plans',
      icon: <TeamIcon />,
      cost: stepCosts[2],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Company & Team: {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Company Structure"
            cost={stepCosts[2]}
            onCostChange={(newCost) => handleCostChange(2, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Savvy Data is organized into four key departments: Product Development, Marketing & Sales, Customer Success, and Operations. Each department is led by an experienced executive who reports directly to the CEO.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Leadership Team
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <TeamIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Rod Garlick, CEO & Co-Founder" 
                  secondary="20+ years in software, product development and data science markting roles at Fortune 500 FinTech and small startup companies" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <TeamIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Nick Baguley, CTO & Co-Founder" 
                  secondary="Repeat Entrepreneur and Machine learning expert with background in developing AI systems for Fortune 500 companies, small startups and across all sectors of the economy." 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <TeamIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Rod Garlick, CMO" 
                  secondary="20+ years experience in B2B SaaS marketing and growth strategy" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
            
            <Typography variant="subtitle1" sx={{ color: '#fff', mt: 3, mb: 1 }}>
              Hiring Plan (18 Months)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
              <MetricChip label="Engineering: 12 hires" />
              <MetricChip label="Sales: 8 hires" />
              <MetricChip label="Customer Success: 6 hires" />
              <MetricChip label="Marketing: 4 hires" />
            </Box>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Financial Plan',
      description: 'Revenue projections, fundraising needs, and financial forecasts',
      icon: <FinanceIcon />,
      cost: stepCosts[3],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Financial Plan: {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Funding Requirements"
            cost={stepCosts[3]}
            onCostChange={(newCost) => handleCostChange(3, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Savvy Data is seeking $5M in Series A funding to accelerate product development, scale marketing efforts, and expand the team. This funding will provide an 18-month runway to achieve key growth milestones.
              </Typography>
            }
          />
          
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              For detailed financial planning, explore our comprehensive financial plan:
            </Typography>
            <Paper 
              sx={{ p: 2, cursor: 'pointer', bgcolor: 'rgba(0, 204, 153, 0.1)', 
                '&:hover': { bgcolor: 'rgba(0, 204, 153, 0.2)' } }}
              onClick={() => setActiveSubStepper('financial')}
            >
              <Typography variant="subtitle1" sx={{ color: '#00CC99' }}>
                View Detailed Financial Plan →
              </Typography>
            </Paper>
          </Box>
        </Box>
      )
    },
    {
      label: 'Marketing Strategy',
      description: 'Go-to-market approach, customer acquisition, and branding',
      icon: <MarketingIcon />,
      cost: stepCosts[4],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Marketing Strategy: {productName}
          </GradientText>
          
          <EditableStepContent
            title="Go-to-Market Strategy"
            cost={stepCosts[4]}
            onCostChange={(newCost) => handleCostChange(4, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Savvy Data will employ a hybrid sales approach combining self-service product-led growth with targeted outbound sales for larger accounts. Our initial focus will be on content marketing to establish thought leadership and drive inbound leads.
              </Typography>
            }
          />
          
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              For detailed marketing planning, explore our comprehensive marketing plan:
            </Typography>
            <Paper 
              sx={{ p: 2, cursor: 'pointer', bgcolor: 'rgba(255, 111, 0, 0.1)', 
                '&:hover': { bgcolor: 'rgba(255, 111, 0, 0.2)' } }}
              onClick={() => setActiveSubStepper('marketing')}
            >
              <Typography variant="subtitle1" sx={{ color: '#FF6F00' }}>
                View Detailed Marketing Plan →
              </Typography>
            </Paper>
          </Box>
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Customer Acquisition Channels
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Content Marketing
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Blog posts, guides, webinars on data analytics and AI topics
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Strategic Partnerships
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Co-marketing with complementary SaaS tools and platforms
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                    Targeted Advertising
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    LinkedIn, Google Ads focusing on decision-makers
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Technology Roadmap',
      description: 'Product development plan and technical infrastructure',
      icon: <TechIcon />,
      cost: stepCosts[5],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Technology Roadmap: {productName}
          </GradientText>
          
          <EditableStepContent
            title="Product Development Timeline"
            cost={stepCosts[5]}
            onCostChange={(newCost) => handleCostChange(5, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Our product roadmap follows an agile development methodology with regular quarterly releases and monthly updates. Major features will be prioritized based on customer feedback and market demand.
              </Typography>
            }
          />
          
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
              For detailed technology planning, explore our comprehensive technology roadmap:
            </Typography>
            <Paper 
              sx={{ p: 2, cursor: 'pointer', bgcolor: 'rgba(138, 43, 226, 0.1)', 
                '&:hover': { bgcolor: 'rgba(138, 43, 226, 0.2)' } }}
              onClick={() => setActiveSubStepper('technology')}
            >
              <Typography variant="subtitle1" sx={{ color: '#8A2BE2' }}>
                View Detailed Technology Roadmap →
              </Typography>
            </Paper>
          </Box>
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Technology Stack
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Frontend
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    React, TypeScript, Material UI for responsive web application
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                    Backend
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Node.js, Python, GraphQL API, Postgres, Redis
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                    AI & ML
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Hugging Face, PyTorch, OpenAI API, Anthropic Claude, custom ML models
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    }
  ];

  // Render the appropriate sub-stepper based on state
  const renderSubStepper = () => {
    switch (activeSubStepper) {
      case 'strategic':
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Strategic Plan</Typography>
            <StrategicPlanStepper 
              companyName={companyName} 
              onComplete={handleSubStepperComplete}
            />
          </Box>
        );
      case 'financial':
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Financial Plan</Typography>
            <FinancialPlanStepper 
              productName={productName}
              onComplete={handleSubStepperComplete}
            />
          </Box>
        );
      case 'marketing':
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Marketing Plan</Typography>
            <MarketingPlanStepper 
              productName={productName}
              onComplete={handleSubStepperComplete}
            />
          </Box>
        );
      case 'product':
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Product Strategy</Typography>
            <ProductStrategyStepper 
              productName={productName}
              onComplete={handleSubStepperComplete}
            />
          </Box>
        );
      case 'technology':
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Technology Roadmap</Typography>
            <TechRoadmapStepper 
              productName={productName}
              onComplete={handleSubStepperComplete}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {activeSubStepper ? (
        renderSubStepper()
      ) : (
        <HolographicStepper 
          steps={businessSteps}
          initialStep={initialStep}
          onComplete={onComplete}
          onSubmitRFP={onSubmitRFP}
          orientation="horizontal"
          alternativeLabel={true}
        />
      )}
    </Box>
  );
};

export default BusinessPlanStepper; 